/**
 * product-gallery.js — product image gallery (PDP + featured-product).
 * The main track is a horizontal scroll-snap container: native one-at-a-time
 * swipe on touch, while prev/next + thumbnails scroll it programmatically.
 * Swiping syncs the active thumbnail back via the scroll listener.
 */
import { Component } from '@theme/component';

class ProductGallery extends Component {
  mounted() {
    const slides = this.refs.slide;
    const thumbs = this.refs.thumb;
    this.slides = Array.isArray(slides) ? slides : slides ? [slides] : [];
    this.thumbs = Array.isArray(thumbs) ? thumbs : thumbs ? [thumbs] : [];
    this.index = 0;

    const track = this.refs.track;
    if (track) {
      this._onScroll = this._onScroll.bind(this);
      track.addEventListener('scroll', this._onScroll, { passive: true });
    }
    this._syncThumbs();
  }

  goto(event, { index }) {
    this._set(parseInt(index, 10) || 0);
  }

  next() {
    if (this.slides.length) this._set((this.index + 1) % this.slides.length);
  }

  prev() {
    if (this.slides.length) this._set((this.index - 1 + this.slides.length) % this.slides.length);
  }

  // Arrows / thumbnails: scroll the track to slide `i` (its own scrollLeft, never
  // scrollIntoView — that would jump the whole page to the gallery on load).
  _set(i) {
    this.index = i;
    const track = this.refs.track;
    if (track) {
      this._programmatic = true;
      track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' });
      clearTimeout(this._settle);
      this._settle = setTimeout(() => { this._programmatic = false; }, 400);
    }
    this._syncThumbs();
  }

  // Native swipe: derive the active slide from scroll position and sync thumbs.
  // Skipped while a programmatic scroll is animating so multi-slide thumb jumps
  // don't flicker through the in-between slides.
  _onScroll() {
    if (this._programmatic) return;
    const track = this.refs.track;
    if (!track || !track.clientWidth) return;
    const i = Math.round(track.scrollLeft / track.clientWidth);
    if (i !== this.index && i >= 0 && i < this.slides.length) {
      this.index = i;
      this._syncThumbs();
    }
  }

  _syncThumbs() {
    this.thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === this.index));

    // Center the active thumb WITHIN the strip only (its own scrollLeft) — never
    // scrollIntoView, which would jump the whole page to the gallery on load.
    const active = this.thumbs[this.index];
    const strip = active && active.parentElement;
    if (active && strip) {
      const stripRect = strip.getBoundingClientRect();
      const thumbRect = active.getBoundingClientRect();
      const offset = thumbRect.left - stripRect.left - (strip.clientWidth - thumbRect.width) / 2;
      strip.scrollTo({ left: strip.scrollLeft + offset, behavior: 'smooth' });
    }
  }
}

customElements.define('product-gallery-component', ProductGallery);
